/**
 * RIGHTS AND COMMUNITY RULES (v1.0.29): the eight rules of "Video guidelines for MoonX authors",
 * each confirmed with a tick, in the guidelines' order and in short words. Each tick is kept on the
 * draft as it is made (`setVideoDraftConfirmations`), and all eight are sent with Submit
 * (`videoConfirmations`); the server refuses a video without them.
 */
import { Anchor, Checkbox, Stack, Text, Title } from '@mantine/core';
import { VIDEO_RULES } from '../../features/cabinet/api/editor-api';
import type { VideoRule } from '../../features/cabinet/api/editor-api';
import { useTranslation } from '../../i18n';
import classes from './video.module.css';

/** The guidelines' page on the website (the Support page links it too). */
export const VIDEO_GUIDELINES_HREF = 'https://moonx.app/for-authors/video-guidelines';

/** The guidelines' two groups: the author's own material, then the community rules. */
const OWN_MATERIAL: readonly VideoRule[] = ['own_rights', 'music_licensed'];

export interface VideoRightsProps {
  readonly confirmed: ReadonlySet<VideoRule>;
  readonly disabled: boolean;
  /**
   * Why the ticks on this page were not kept, when they were not: the ticks stay on the page, and
   * the sentence says whether the next tick tries again or what refused them.
   */
  readonly notKept?: string | undefined;
  readonly onToggle: (rule: VideoRule, confirmed: boolean) => void;
}

export function VideoRights({ confirmed, disabled, notKept, onToggle }: VideoRightsProps) {
  const { t } = useTranslation('video');
  const done = VIDEO_RULES.filter((rule) => confirmed.has(rule)).length;
  const group = (rules: readonly VideoRule[]) => (
    <ul className={classes.rights}>
      {rules.map((rule) => (
        <li key={rule}>
          <Checkbox
            checked={confirmed.has(rule)}
            disabled={disabled}
            label={t(`rights.rules.${rule}.label`)}
            description={t(`rights.rules.${rule}.note`)}
            onChange={(event) => {
              onToggle(rule, event.currentTarget.checked);
            }}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <Stack component="section" gap="sm" aria-labelledby="video-rights-title">
      <Stack gap={2}>
        <Title id="video-rights-title" order={2} size="h5">
          {t('rights.title')}
        </Title>
        <Text size="xs" c="dimmed" role="status">
          {t('rights.count', { done: String(done), total: String(VIDEO_RULES.length) })}
        </Text>
        <Text size="xs" c="dimmed">
          {t('rights.intro')}
        </Text>
        {notKept === undefined ? null : (
          <Text size="xs" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
            {notKept}
          </Text>
        )}
      </Stack>
      <Text className={classes.groupLabel}>{t('rights.ownMaterial')}</Text>
      {group(OWN_MATERIAL)}
      <Text className={classes.groupLabel}>{t('rights.community')}</Text>
      {group(VIDEO_RULES.filter((rule) => !OWN_MATERIAL.includes(rule)))}
      <Text size="xs" c="dimmed">
        {t('rights.checked')}{' '}
        <Anchor href={VIDEO_GUIDELINES_HREF} target="_blank" rel="noreferrer" size="xs">
          {t('rights.guidelines')}
        </Anchor>
      </Text>
    </Stack>
  );
}
